import { useNavigate } from "react-router-dom";

import {
  DialogBody,
  DialogContent,
  DialogRoot,
} from "../../components/ui/dialog";
import { GameOptions } from "./GameOptions";
import { useEffect, useState } from "react";
import { Box, Button, Heading, Text, Image } from "@chakra-ui/react";
import fetchRegions from "@/services/fetch_regions";
import { trackEvent } from "@/utils/analytics";
import { useTranslation } from "react-i18next";
import Snowfall from "react-snowfall";
import { christmasEnabled } from "@/utils/feature_flags";
import { SplitFlapDisplay } from "@/components/SplitFlapDisplay/SplitFlapDisplay";
import { Ad } from "@/components/Ad/ad";
import { DailyPromoButton } from "@/components/DailyPromoButton/DailyPromoButton";

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [showOptions, setShowOptions] = useState(false);
  const [regions, setRegions] = useState<
    { name: string; emoji: string; code: string }[] | null
  >(null);

  const [defaults, setDefaults] = useState<{
    region: "usa" | "eur" | "world";
    difficulty: "easy" | "medium" | "hard";
  }>({
    region: "eur",
    difficulty: "medium",
  });

  const [bgImageIndex, setBgImageIndex] = useState(-1);

  const bgImages = {
    usa: [
      "atl.jpg",
      "bna.jpg",
      "bos.jpg",
      "den.jpg",
      "dfw.jpg",
      "iad.jpg",
      "jfk.jpg",
      "las.jpg",
      "lax.jpg",
      "msp.jpg",
      "phx.jpg",
      "san.jpg",
      "sat.jpg",
      "sea.jpg",
      "sfo.jpg",
    ],
  };

  useEffect(() => {
    fetchRegions().then((data) => {
      setRegions(data.regions);
    });

    const region = localStorage.getItem("selected_region");
    const difficulty = localStorage.getItem("selected_difficulty");
    setDefaults({
      region: region as "usa" | "eur" | "world",
      difficulty: difficulty as "easy" | "medium" | "hard",
    });
  }, []);

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * bgImages.usa.length);
    setBgImageIndex(randomIndex);
  }, []);

  useEffect(() => {
    if (showOptions) {
      trackEvent("play_clicked");
    }
  }, [showOptions]);

  return (
    <DialogRoot lazyMount open={showOptions} size="full">
      <DialogContent background="blackAlpha.900">
        <DialogBody
          w="100%"
          display="flex"
          alignItems={{ base: "start", md: "center" }}
          justifyContent="center"
        >
          <GameOptions
            regions={regions ?? []}
            defaults={defaults}
            onClose={() => setShowOptions(false)}
            onStartGame={(selectedDifficulty, selectedRegion) => {
              localStorage.setItem("selected_region", selectedRegion);
              localStorage.setItem("selected_difficulty", selectedDifficulty);

              navigate(`/play`);
            }}
          />
        </DialogBody>
      </DialogContent>

      <div style={{ position: "relative", width: "100vw", height: "100vh" }}>
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          backgroundColor="rgba(0, 0, 0, 0.7)"
        />
        {bgImageIndex !== -1 && (
          <Image
            src={`/bg/usa/${bgImages.usa[bgImageIndex]}`}
            alt=""
            loading="lazy"
            width="100%"
            height="100%"
            objectFit="cover"
          />
        )}
        {christmasEnabled && <Snowfall snowflakeCount={200} />}

        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          w={{ base: "full", md: "800px" }}
          mx="auto"
          px={4}
          height="100vh"
          display="flex"
          flexDirection="column"
          pt={{ base: 10, md: 100 }}
          color="white"
        >
          <Heading
            size={{ base: "5xl", md: "7xl" }}
            as="h1"
            position="relative"
            display="inline-block"
          >
            {christmasEnabled && (
              <Image
                src="/santa-hat.png"
                alt="Santa Hat"
                position="absolute"
                w={{ base: "40px", lg: "60px" }}
                h={{ base: "40px", lg: "60px" }}
                top={{ base: "-20px", md: "-12px", lg: "-25px" }}
                left={{ base: "-15px", md: "-15px", lg: "-25px" }}
                transform="rotate(-25deg)"
              />
            )}
            Flight Guesser
          </Heading>

          <Box display={{ base: "block", md: "none" }}>
            <Heading size={{ base: "lg", md: "xl" }}>
              {t("home.exploreTheSkies")}
            </Heading>
          </Box>
          <Box display={{ base: "none", md: "block" }}>
            <SplitFlapDisplay text={t("home.exploreTheSkies")} />
          </Box>

          <Text
            fontSize={{ base: "xl", md: "2xl" }}
            fontFamily="sans-serif"
            mt={5}
          >
            {t("home.instructions.get5RandomFlights")}
          </Text>
          <Text
            fontSize={{ base: "xl", md: "2xl" }}
            fontFamily="sans-serif"
            mt={5}
          >
            {t("home.instructions.startWith1000Points")}
          </Text>
          <Button
            w="full"
            size="lg"
            mt={10}
            h="60px"
            onClick={() => {
              setShowOptions(true);
            }}
            backgroundColor="white"
            color="black"
            fontSize="2xl"
            style={{
              border: christmasEnabled ? "4px solid" : undefined,
              borderImage: christmasEnabled
                ? "repeating-linear-gradient(45deg, #ff0000, #ff0000 10px, #ffffff 10px, #ffffff 20px) 8"
                : undefined,
              borderImageSlice: christmasEnabled ? "1" : undefined,
            }}
            _hover={{
              transform: christmasEnabled ? "scale(1.02)" : "",
            }}
          >
            {t("home.playButton")}
          </Button>
          <DailyPromoButton from="home" />
          <Ad mt={10} slot="5753575506" />
        </Box>
      </div>
    </DialogRoot>
  );
};

export default Home;
